import { createContext, ReactNode, useEffect, useMemo, useState } from 'react';
import {
  COMPACT_MAX_HEIGHT_PX,
  PRESENTATION_TOKENS,
  PresentationMode,
  PresentationTokens,
} from './presentation';

export interface PresentationContextType {
  mode: PresentationMode;
  isCompact: boolean;
  tokens: PresentationTokens;
}

/**
 * Regular-mode default so `usePresentation` never throws and screens rendered
 * outside a provider (tests, legacy routes, the error boundary) degrade to
 * regular. This is a deliberate departure from useConfig/useDevice.
 */
const REGULAR_CONTEXT: PresentationContextType = {
  mode: 'regular',
  isCompact: false,
  tokens: PRESENTATION_TOKENS.regular,
};

export const PresentationContext =
  createContext<PresentationContextType>(REGULAR_CONTEXT);

/**
 * Resolves the presentation mode from the available space and provides it to
 * the tree.
 *
 * Resolution order:
 * 1. An explicit `isCompact` prop always wins (manual override).
 * 2. Otherwise a `ResizeObserver` on a fixed, content-independent sensor
 *    measures the available height; compact when it is <= COMPACT_MAX_HEIGHT_PX.
 */
export function PresentationProvider({
  isCompact,
  children,
}: {
  isCompact?: boolean;
  children: ReactNode;
}) {
  // Callback ref so the effect re-runs once the sensor element has mounted.
  const [sensor, setSensor] = useState<HTMLDivElement | null>(null);
  const [measuredCompact, setMeasuredCompact] = useState(
    () => window.innerHeight <= COMPACT_MAX_HEIGHT_PX,
  );

  useEffect(() => {
    // Explicit override wins — no need to measure available space.
    if (isCompact !== undefined || typeof ResizeObserver === 'undefined') {
      return;
    }
    if (!sensor) {
      return;
    }

    const observer = new ResizeObserver(([entry]) => {
      if (!entry) {
        return;
      }
      setMeasuredCompact(entry.contentRect.height <= COMPACT_MAX_HEIGHT_PX);
    });
    observer.observe(sensor);

    return () => observer.disconnect();
  }, [isCompact, sensor]);

  const value = useMemo<PresentationContextType>(() => {
    const compact = isCompact ?? measuredCompact;
    const mode: PresentationMode = compact ? 'compact' : 'regular';
    return { mode, isCompact: compact, tokens: PRESENTATION_TOKENS[mode] };
  }, [isCompact, measuredCompact]);

  return (
    <PresentationContext.Provider value={value}>
      <div
        ref={setSensor}
        data-testid="presentation-sensor"
        aria-hidden="true"
        style={{
          position: 'fixed',
          inset: 0,
          visibility: 'hidden',
          pointerEvents: 'none',
        }}
      />
      {children}
    </PresentationContext.Provider>
  );
}
